import axiosServices from "../utils/axios";

import type {
  CreateHusbandryTaskPayload,
  HusbandryQuickTask,
  HusbandryTask,
  HusbandryTaskStatus,
  UpdateHusbandryTaskPayload,
} from "../utils/types/husbandryTasks";

// API response types (snake_case from backend)
interface HusbandryTaskResponse {
  husbandry_task_id: string;
  title: string;
  description: string;
  agent_id: string;
  vessel_id: string;
  due_date: string | null;
  status: HusbandryTaskStatus;
  created_dt: string;
  modified_dt: string;
  created_by: string;
  job_id?: string;
  berth_id?: string | null;
  completed_at?: string | null;
  comments?: string;
  job?: {
    job_id: string;
    status_id: string;
    current_status: string;
    voyage: string;
    assigned_agent_id?: string;
    status: {
      pick_list_id: string;
      value: string;
    };
    vessel: {
      vessel_id: string;
      name: string;
    };
    assigned_agent?: {
      person_id: string;
      name: string;
      email: string;
    };
  };
}

interface FetchHusbandryTasksResponse {
  data: HusbandryTaskResponse[];
  total: number;
}

const transformHusbandryTask = (taskResponse: HusbandryTaskResponse): HusbandryTask => ({
  id: taskResponse.husbandry_task_id,
  title: taskResponse.title,
  description: taskResponse.description,
  agentId: taskResponse.agent_id,
  vesselId: taskResponse.vessel_id,
  dueDate: taskResponse.due_date,
  status: taskResponse.status,
  createdAt: taskResponse.created_dt,
  updatedAt: taskResponse.modified_dt,
  createdBy: taskResponse.created_by,
  jobId: taskResponse.job_id,
  berthId: taskResponse.berth_id ?? undefined,
  completedAt: taskResponse.completed_at,
  comments: taskResponse.comments,
  job: taskResponse.job,
});

export const fetchHusbandryTasks = async (params?: {
  search?: string;
  agentId?: string;
  vesselStatus?: string;
  jobId?: string;
}): Promise<{ data: HusbandryTask[]; total: number }> => {
  const queryParams: Record<string, any> = {};

  if (params?.search) {
    queryParams.q = params.search;
  }
  if (params?.agentId) {
    queryParams.agent_id = params.agentId;
  }
  if (params?.vesselStatus) {
    queryParams.vessel_status = params.vesselStatus;
  }
  if (params?.jobId) {
    queryParams.job_id = params.jobId;
  }

  const response = await axiosServices.get<FetchHusbandryTasksResponse>("/husbandry-tasks", {
    params: queryParams,
  });

  return {
    data: response.data.data.map(transformHusbandryTask),
    total: response.data.total,
  };
};

export const createHusbandryTask = async (task: CreateHusbandryTaskPayload): Promise<HusbandryTask> => {
  const response = await axiosServices.post<HusbandryTaskResponse>("/husbandry-tasks", {
    title: task.title,
    description: task.description,
    agent_id: task.agentId,
    due_date: task.dueDate,
    status: task.status,
    job_id: task.jobId,
    berth_id: task.berthId,
    comments: task.comments,
  });
  return transformHusbandryTask(response.data);
};

export const updateHusbandryTask = async (task: UpdateHusbandryTaskPayload): Promise<HusbandryTask> => {
  const payload: Record<string, any> = {};

  if (task.title !== undefined) payload.title = task.title;
  if (task.description !== undefined) payload.description = task.description;
  if (task.agentId !== undefined) payload.agent_id = task.agentId;
  if (task.dueDate !== undefined) payload.due_date = task.dueDate;
  if (task.status !== undefined) payload.status = task.status;
  if (task.jobId !== undefined) payload.job_id = task.jobId;
  if (task.berthId !== undefined) payload.berth_id = task.berthId;
  if (task.comments !== undefined) payload.comments = task.comments;

  const response = await axiosServices.put<HusbandryTaskResponse>(`/husbandry-tasks/${task.id}`, payload);
  return transformHusbandryTask(response.data);
};

export const updateHusbandryTaskStatus = async (
  taskId: string,
  status: HusbandryTaskStatus,
): Promise<HusbandryTask> => {
  const payload: Record<string, any> = { status };

  if (status === "completed") {
    payload.completed_at = new Date().toISOString();
  }

  const response = await axiosServices.put<HusbandryTaskResponse>(`/husbandry-tasks/${taskId}`, payload);
  return transformHusbandryTask(response.data);
};

export const deleteHusbandryTask = async (taskId: string): Promise<void> => {
  await axiosServices.delete(`/husbandry-tasks/${taskId}`);
};

export const fetchHusbandryQuickTasks = async (): Promise<HusbandryQuickTask[]> => {
  const response = await axiosServices.get("/quicktask");
  return response.data.data;
};
